/* eslint-disable */
// Scheduled tasks ("Repeat this") for Server Chat. Contract: console2Backend
// CODE_BASE_DETAILS/IMPLEMENTATION_DETAILS/serverChatSchedules/API_CONTRACT.md. All calls go through the
// page's `run(action, body)` helper. Never show cost, never say "cron"/"job".
import { useCallback, useEffect, useRef, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import MarkdownView from './MarkdownView';
import pageStyles from './ServerChatPage.module.scss';
import s from './Schedules.module.scss';

// Copies text; if the browser refuses, selects the text in `ref` (or, with no ref, shows it in a prompt to copy by hand).
export function useCopyText(text, ref) {
	const [done, setDone] = useState(false);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setDone(true);
			setTimeout(() => setDone(false), 1500);
		} catch (e) {
			if (ref?.current) {
				const sel = window.getSelection();
				const range = document.createRange();
				range.selectNodeContents(ref.current);
				sel.removeAllRanges();
				sel.addRange(range);
			} else window.prompt('Copy this:', text);
		}
	};
	return { done, copy };
}

// One command in a code box with a Copy button.
export function CopyLine({ text, label }) {
	const ref = useRef(null);
	const { done, copy } = useCopyText(text, ref);
	return (
		<div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
			<div className={pageStyles.mdPre} style={{ flex: 1, minWidth: 0 }}>
				<pre><code ref={ref}>{text}</code></pre>
			</div>
			<button type='button' className={pageStyles.btnGhost} onClick={copy} aria-label={label}>{done ? 'Copied' : 'Copy'}</button>
		</div>
	);
}

const TEST_POLL_MS = 2500;
const LIST_POLL_MS = 15000;
const HIST_POLL_MS = 5000;
const TEST_MAX_MS = 10 * 60 * 1000;
export const REPEAT_THIS_TEXT = 'Repeat this: make a scheduled task from this chat.';
// Effort levels the scheduled run accepts (contract section 3).
const SCHED_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const KINDS = [
	['daily', 'Every day'], ['weekdays', 'Weekdays (Mon–Fri)'], ['hours', 'Every few hours'],
	['weekly', 'Some days of the week'], ['monthly', 'Once a month'],
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Time with the zone name, and the year when it is not this year. tz = the task's zone (falls back to the browser's).
const fullTime = (sec, tz) => {
	if (!sec) return '';
	const d = new Date(sec * 1000);
	const o = { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
	if (d.getFullYear() !== new Date().getFullYear()) o.year = 'numeric';
	try { return d.toLocaleString([], tz ? { ...o, timeZone: tz } : o); } catch (e) { return d.toLocaleString([], o); }
};

// Efforts: the words people see -> the values the server takes (contract section 3).
const EFFORT_LABELS = { low: 'Quick', medium: 'Normal', high: 'Thorough', xhigh: 'Very thorough', max: 'Maximum' };
const effortLabel = (e) => EFFORT_LABELS[e] || e;

// Drafts the user already approved, remembered per browser so a reload does not offer Approve twice.
const APPROVED_LS = 'serverChat.approvedDrafts';
export const draftKey = (chatId, i) => `${chatId}:${i}`;
export function loadApprovedDrafts() {
	try { return JSON.parse(localStorage.getItem(APPROVED_LS) || '{}') || {}; } catch (e) { return {}; }
}
export function rememberApprovedDraft(key) {
	try { localStorage.setItem(APPROVED_LS, JSON.stringify({ ...loadApprovedDrafts(), [key]: Date.now() })); } catch (e) { /* private mode: fine */ }
}

// ---- schedule-draft block in a bot reply ----
// Returns the text without the LAST ```schedule-draft block, and the checked draft (null if none / unusable).
// A block that does not parse or has the wrong shape is left in the text so nothing is hidden silently;
// a block still being written (no closing fence yet) is cut off.
const isStr = (x) => typeof x === 'string';
function checkDraft(d) {
	if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
	if (!isStr(d.title) || !isStr(d.prompt)) return null;
	if (!d.repeat || typeof d.repeat !== 'object' || !isStr(d.repeat.kind) || !d.repeat.kind) return null;
	if (d.emailTo != null && !(Array.isArray(d.emailTo) && d.emailTo.every(isStr))) return null;
	return { ...d, emailTo: d.emailTo || [] };
}
export function splitDraft(text) {
	const src = text || '';
	const re = /```schedule-draft[^\n]*\n([\s\S]*?)```/g;
	let m; let last = null;
	while ((m = re.exec(src))) last = m;
	const open = src.lastIndexOf('```schedule-draft');
	if (open > (last ? last.index : -1)) return { body: src.slice(0, open).trim(), draft: null };
	if (!last) return { body: src, draft: null };
	let draft = null;
	try { draft = checkDraft(JSON.parse(last[1])); } catch (e) { draft = null; }
	if (!draft) return { body: src, draft: null };
	return { body: (src.slice(0, last.index) + src.slice(last.index + last[0].length)).trim(), draft };
}

// Plain wording for a run status (contract 1.3).
export function runStatusText(r) {
	if (!r) return '';
	const why = r.reason ? `: ${r.reason}` : '';
	switch (r.status) {
		case 'queued': return 'Waiting its turn';
		case 'running': return 'Running';
		case 'ok': return 'OK';
		case 'failed': return `Failed${why}`;
		case 'stopped_steps': return 'Stopped: too many steps';
		case 'did_not_finish': return 'Did not finish';
		case 'skipped': return `Skipped${why}`;
		case 'held': return "Held: may contain another customer's data";
		default: return r.status || '';
	}
}
const runTone = (st) => (st === 'ok' ? s.toneOk : ['queued', 'running'].includes(st) ? s.toneInfo
	: st === 'skipped' ? s.toneWarn : s.toneBad);

// ---- live "Runs every…" sentence from the server's calculator ----
function useRepeatPreview(run, repeat, timeZone) {
	const [p, setP] = useState(null);
	const key = JSON.stringify([repeat, timeZone]);
	useEffect(() => {
		setP(null);   // drop the old sentence while the new one is worked out
		if (!repeat) return undefined;
		let live = true;
		const t = setTimeout(() => {
			run('schedPreviewRepeat', { repeat, ...(timeZone ? { timeZone } : {}) }, { quiet: true })
				.then((d) => live && setP(d ? { ok: true, ...d } : null))
				.catch((e) => live && setP({ ok: false, err: String(e || '') }));
		}, 350);
		return () => { live = false; clearTimeout(t); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);
	return p;
}

function RepeatSentence({ preview, fallback }) {
	if (preview && !preview.ok) return <span className={s.repeatBad}>{preview.err || 'Check the repeat settings.'}</span>;
	const text = preview?.repeatText || fallback;
	const next = preview?.nextRunsText?.[0];
	if (!text) return <span className={pageStyles.muted}>Working out the next run…</span>;
	return <span>{text}{next ? <> — next: <strong>{next}</strong></> : ''}</span>;
}

// ---- test run: start + poll until it finishes ----
function useTestRun(run) {
	const [test, setTest] = useState(null);   // T from the contract
	const [sig, setSig] = useState('');       // what the test was run on (prompt/model/effort)
	const [starting, setStarting] = useState(false);
	const id = test?.id;
	const going = test && ['waiting', 'running'].includes(test.status);
	const fails = useRef(0);
	const startedAt = useRef(0);
	useEffect(() => {
		if (!going) return undefined;
		const lost = () => setTest((o) => (o && o.id === id ? { ...o, status: 'failed', reason: 'Lost contact with the server. Try again.' } : o));
		const t = setInterval(() => {
			if (Date.now() - startedAt.current > TEST_MAX_MS) { lost(); return; }
			run('schedTestRunGet', { testRunId: id }, { quiet: true })
				.then((d) => { fails.current = 0; if (d?.testRun) setTest(d.testRun); })
				.catch(() => { fails.current += 1; if (fails.current >= 5) lost(); });
		}, TEST_POLL_MS);
		return () => clearInterval(t);
	}, [going, id, run]);
	const start = (draft) => {
		fails.current = 0; startedAt.current = Date.now();
		setStarting(true);
		return run('schedTestRun', { draft })
			.then((d) => { if (d?.testRun) { setTest(d.testRun); setSig(testSig(draft)); } })
			.catch(() => {})
			.finally(() => setStarting(false));
	};
	return { test, sig, start, starting, going, reset: () => { setTest(null); setSig(''); } };
}
// The server requires a fresh OK test when prompt, model, effort or the customer-facing switch change.
const testSig = (d) => JSON.stringify([d?.prompt || '', d?.model || 'opus', d?.effort || 'medium', !!d?.customerFacing]);

function TestRunResult({ test }) {
	const [showAnswer, setShowAnswer] = useState(false);
	if (!test) return null;
	const going = ['waiting', 'running'].includes(test.status);
	return (
		<div className={`${s.testBox} ${test.status === 'failed' ? s.testBad : ''}`} aria-live='polite'>
			<div className={s.testHead}>
				{going ? <span className={pageStyles.dots}><i /><i /><i /></span>
					: test.status === 'ok' ? <CheckRoundedIcon fontSize='inherit' className={s.okIcon} /> : <CloseRoundedIcon fontSize='inherit' />}
				<strong>
					{test.status === 'waiting' ? 'Test run is waiting its turn…'
						: test.status === 'running' ? `Test run in progress${test.steps ? ` · ${test.steps} steps so far` : ''}…`
							: test.status === 'ok' ? 'Test run looks good' : `Test run failed${test.reason ? `: ${test.reason}` : ''}`}
				</strong>
			</div>
			{going && <div className={pageStyles.muted}>This can take a few minutes. You can keep this open.</div>}
			{!!test.email && (
				<div className={s.receiptRows}>
					<Row k='Test email to'>{(test.email.to || []).join(', ')} <span className={pageStyles.muted}>(only you, for the test)</span></Row>
					<Row k='Subject'>{test.email.subject}</Row>
				</div>
			)}
			{!!test.email?.html && test.status === 'ok' && (
				<iframe title='Email preview' className={s.emailFrame} sandbox='' srcDoc={test.email.html} />
			)}
			{!!test.reply && !going && (
				<>
					<button type='button' className={s.linkBtn} onClick={() => setShowAnswer((v) => !v)} aria-expanded={showAnswer}>
						{showAnswer ? '▾ Hide the answer' : '▸ Show the answer'}
					</button>
					{showAnswer && <div className={s.answerBox}><MarkdownView text={test.reply} /></div>}
				</>
			)}
		</div>
	);
}

// On/off switch shared by the form and the approve card.
function CustomerFacingSwitch({ value, onChange }) {
	return (
		<label className={pageStyles.check}>
			<input type='checkbox' checked={!!value} onChange={(e) => onChange(e.target.checked)} /> Customer-facing answer
			<span className={pageStyles.muted}> Off: technical answer with queries and proof. On: plain business words for customers.</span>
		</label>
	);
}

function Row({ k, children }) {
	return (
		<div className={s.row}>
			<span className={s.rowKey}>{k}</span>
			<span className={s.rowVal}>{children}</span>
		</div>
	);
}

const modelLabel = (models, id) => (models.find((m) => m.id === id) || models[0]).label;

// ---- summary card for a bot-made draft, shown inside the chat ----
export function DraftCard({ draft, run, models, sourceChatId, onApproved, onEdit, onCancel }) {
	const [customerFacing, setCustomerFacing] = useState(!!draft.customerFacing);
	const full = { ...draft, model: draft.model || 'opus', effort: draft.effort || 'medium', customerFacing, sourceChatId };
	const preview = useRepeatPreview(run, full.repeat, full.timeZone);
	const t = useTestRun(run);
	const [approving, setApproving] = useState(false);
	const [showPrompt, setShowPrompt] = useState(false);
	const [done, setDone] = useState(false);
	const testOk = t.test?.status === 'ok' && t.sig === testSig(full);
	const approve = () => {
		setApproving(true);
		run('schedApproveDraft', { draft: full, testRunId: t.test.id })
			.then((d) => { if (d?.schedule) { setDone(true); onApproved(d.schedule); } })
			.catch(() => {})
			.finally(() => setApproving(false));
	};
	const to = full.emailTo || [];
	return (
		<section className={s.draftCard} aria-label='New scheduled task, waiting for your approval'>
			<div className={s.draftHead}>
				<ScheduleRoundedIcon fontSize='small' aria-hidden className={s.accentIcon} />
				<div className={s.draftTitleWrap}>
					<div className={s.kicker}>New scheduled task · not saved yet</div>
					<div className={s.draftTitle}>{full.title || 'Untitled task'}</div>
				</div>
			</div>
			<div className={s.receiptRows}>
				<Row k='Repeats'><RepeatSentence preview={preview} /></Row>
				<Row k='Asks'>
					<span className={showPrompt ? '' : s.clamp}>{full.prompt}</span>
					{(full.prompt || '').length > 140 && (
						<button type='button' className={s.linkBtn} onClick={() => setShowPrompt((v) => !v)}>{showPrompt ? 'Show less' : 'Show all'}</button>
					)}
				</Row>
				<Row k='Email'>{to.length ? <>{to.join(', ')}<div className={pageStyles.muted}>Subject: {full.emailSubject || '—'}</div></> : 'No email — saved as a chat only'}</Row>
				{!!to.length && <Row k='Send when'>{full.emailOnlyIfChanged ? 'Only if something changed' : 'Every run'}</Row>}
				<Row k='Model'>{modelLabel(models, full.model)} · {effortLabel(full.effort)}</Row>
			</div>
			<CustomerFacingSwitch value={customerFacing} onChange={setCustomerFacing} />
			<TestRunResult test={t.test} />
			<div className={s.actions}>
				<button type='button' className={pageStyles.btnGhost} onClick={() => t.start(full)} disabled={t.starting || t.going}
					title='Runs it once now so you can check the answer. Counts toward today’s runs.'>
					<ScienceRoundedIcon fontSize='inherit' aria-hidden /> {t.test ? 'Test again' : 'Test run'}
				</button>
				<span title={testOk ? '' : 'Run a test first. Approve unlocks when the test looks good.'}>
					<button type='button' className={pageStyles.btnPrimary} onClick={approve} disabled={!testOk || approving || done}>
						{approving ? 'Saving…' : 'Approve'}
					</button>
				</span>
				<button type='button' className={pageStyles.btnGhost} onClick={() => onEdit(full)}>
					<EditRoundedIcon fontSize='inherit' aria-hidden /> Edit
				</button>
				<button type='button' className={pageStyles.btnGhost} onClick={onCancel}>Cancel</button>
			</div>
			{!testOk && <div className={s.why}>Test it once, then approve. Nothing repeats until you approve.</div>}
		</section>
	);
}

// ---- repeat picker ----
function repeatFromForm(f) {
	switch (f.kind) {
		case 'hours': return { kind: 'hours', everyHours: Math.max(1, Math.min(24, parseInt(f.everyHours, 10) || 1)),
			...(f.windowOn ? { window: { from: f.from, to: f.to } } : {}) };
		case 'weekly': return { kind: 'weekly', time: f.time, days: f.days };
		case 'monthly': return { kind: 'monthly', time: f.time, monthDay: Math.max(1, Math.min(31, parseInt(f.monthDay, 10) || 1)) };
		default: return { kind: f.kind, time: f.time };
	}
}
function formFromRepeat(r) {
	const base = { kind: 'weekdays', time: '09:00', days: ['mon'], everyHours: 2, windowOn: false, from: '08:00', to: '20:00', monthDay: 1 };
	if (!r) return base;
	return { ...base, kind: r.kind || base.kind, time: r.time || base.time, days: r.days?.length ? r.days : base.days,
		everyHours: r.everyHours || base.everyHours, windowOn: !!r.window, from: r.window?.from || base.from,
		to: r.window?.to || base.to, monthDay: r.monthDay || base.monthDay };
}

function RepeatPicker({ f, set }) {
	const toggleDay = (d) => set({ days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] });
	return (
		<div className={s.repeatBox}>
			<div className={s.segWrap} role='group' aria-label='How often'>
				{KINDS.map(([k, label]) => (
					<button key={k} type='button' aria-pressed={f.kind === k}
						className={`${s.seg} ${f.kind === k ? s.segOn : ''}`} onClick={() => set({ kind: k })}>{label}</button>
				))}
			</div>
			{f.kind === 'weekly' && (
				<div className={s.segWrap} role='group' aria-label='Days'>
					{DAYS.map(([d, label]) => (
						<button key={d} type='button' aria-pressed={f.days.includes(d)}
							className={`${s.seg} ${s.segDay} ${f.days.includes(d) ? s.segOn : ''}`} onClick={() => toggleDay(d)}>{label}</button>
					))}
				</div>
			)}
			<div className={s.inline}>
				{f.kind === 'hours' ? (
					<>
						<label className={s.inlineLabel}>Every
							<input type='number' min={1} max={24} value={f.everyHours} className={s.num}
								onChange={(e) => set({ everyHours: e.target.value })} aria-label='Hours between runs' /> hour(s)
						</label>
						<label className={s.inlineLabel}>
							<input type='checkbox' checked={f.windowOn} onChange={(e) => set({ windowOn: e.target.checked })} /> Only between
						</label>
						<input type='time' value={f.from} disabled={!f.windowOn} onChange={(e) => set({ from: e.target.value })} aria-label='From' />
						<span>and</span>
						<input type='time' value={f.to} disabled={!f.windowOn} onChange={(e) => set({ to: e.target.value })} aria-label='To' />
					</>
				) : (
					<>
						{f.kind === 'monthly' && (
							<label className={s.inlineLabel}>On day
								<input type='number' min={1} max={31} value={f.monthDay} className={s.num}
									onChange={(e) => set({ monthDay: e.target.value })} aria-label='Day of the month' />
							</label>
						)}
						<label className={s.inlineLabel}>at
							<input type='time' value={f.time} onChange={(e) => set({ time: e.target.value })} aria-label='Time' />
						</label>
					</>
				)}
			</div>
			{f.kind === 'monthly' && f.monthDay > 28 && <div className={pageStyles.muted}>In shorter months it runs on the last day.</div>}
		</div>
	);
}

// ---- create / edit form ----
function ScheduleForm({ run, models, initial, existing, dirtyRef, onSaved, onClose }) {
	const [title, setTitle] = useState(initial?.title || '');
	const [prompt, setPrompt] = useState(initial?.prompt || '');
	const [model, setModel] = useState(initial?.model || 'opus');
	const [effort, setEffort] = useState(initial?.effort || 'medium');
	const [rf, setRf] = useState(() => formFromRepeat(initial?.repeat));
	const [emailOn, setEmailOn] = useState(!!initial?.emailTo?.length);
	const [emails, setEmails] = useState((initial?.emailTo || []).join(', '));
	const [subject, setSubject] = useState(initial?.emailSubject || '');
	const [onlyIfChanged, setOnlyIfChanged] = useState(!!initial?.emailOnlyIfChanged);
	const [attachFiles, setAttachFiles] = useState(initial?.attachFiles !== false);
	const [customerFacing, setCustomerFacing] = useState(!!initial?.customerFacing);
	const [suggesting, setSuggesting] = useState(false);
	const [saving, setSaving] = useState(false);
	const t = useTestRun(run);
	const repeat = repeatFromForm(rf);
	const preview = useRepeatPreview(run, repeat, initial?.timeZone);
	const emailList = emails.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
	const badEmail = emailList.find((x) => !EMAIL_RE.test(x));

	const draft = {
		title: title.trim(), prompt: prompt.trim(), model, effort, repeat,
		emailTo: emailOn ? emailList : [], emailSubject: emailOn ? subject.trim() : '',
		emailOnlyIfChanged: emailOn && onlyIfChanged, attachFiles, customerFacing,
		...(initial?.timeZone ? { timeZone: initial.timeZone } : {}),
		...(initial?.customerId ? { customerId: initial.customerId } : {}),
		...(initial?.sourceChatId ? { sourceChatId: initial.sourceChatId } : {}),
	};
		// Lets the tab ask "has this form been changed?" before throwing it away.
	const baseSig = useRef(null);
	if (baseSig.current === null) baseSig.current = JSON.stringify(draft);
	useEffect(() => { if (dirtyRef) dirtyRef.current = JSON.stringify(draft) !== baseSig.current; });
	useEffect(() => () => { if (dirtyRef) dirtyRef.current = false; }, [dirtyRef]);
	// Editing: a new test is needed only when what Claude is asked (or how) changes.
	const askChanged = !existing || testSig(draft) !== testSig(existing);
	const testOk = t.test?.status === 'ok' && t.sig === testSig(draft);

	const problem = !draft.title ? 'Give it a title.'
		: !draft.prompt ? 'Write what Claude should do each time.'
			: rf.kind === 'weekly' && !rf.days.length ? 'Pick at least one day.'
				: emailOn && !emailList.length ? 'Add at least one email address, or turn email off.'
					: emailOn && badEmail ? `"${badEmail}" doesn't look like an email address.`
						: emailOn && emailList.length > 20 ? 'Up to 20 email addresses.'
							: emailOn && !draft.emailSubject ? 'Give the email a subject (or press Suggest).'
								: preview && !preview.ok ? 'Check the repeat settings.' : '';
	const needTest = askChanged && !testOk;
	const saveWhy = problem || (needTest ? 'Run a test first. Save unlocks when the test looks good.' : '');

	const suggest = () => {
		setSuggesting(true);
		run('schedSuggestSubject', { title: draft.title, prompt: draft.prompt, ...(initial?.customerId ? { customerId: initial.customerId } : {}) })
			.then((d) => d?.subject && setSubject(d.subject)).catch(() => {}).finally(() => setSuggesting(false));
	};
	const save = () => {
		if (saveWhy) return;
		setSaving(true);
		const req = existing
			? run('schedUpdate', { scheduleId: existing.id, changes: draft, ...(askChanged ? { testRunId: t.test.id } : {}) })
			: run('schedApproveDraft', { draft, testRunId: t.test.id });
		req.then((d) => d?.schedule && onSaved(d.schedule)).catch(() => {}).finally(() => setSaving(false));
	};

	return (
		<section className={`${pageStyles.card} ${s.form}`} aria-label={existing ? 'Edit scheduled task' : 'New scheduled task'}>
			<div className={s.formHead}>
				<div className={pageStyles.cardTitle}>{existing ? 'Edit scheduled task' : 'New scheduled task'}</div>
				<button type='button' className={pageStyles.iconBtn} onClick={onClose} title='Close' aria-label='Close'><CloseRoundedIcon fontSize='small' /></button>
			</div>
			<label className={pageStyles.block}>Title
				<input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. Pending indents' />
			</label>
			<label className={pageStyles.block}>What should Claude do each time?
				<textarea rows={4} maxLength={4000} value={prompt} onChange={(e) => setPrompt(e.target.value)}
					placeholder="e.g. List today's pending indents per store, oldest first." />
			</label>
			<div className={pageStyles.block}>
				<span>Model</span>
				<div className={s.segWrap} role='group' aria-label='Model'>
						{models.map((m) => (
							<button key={m.id} type='button' aria-pressed={model === m.id} title={m.hint}
								className={`${s.seg} ${model === m.id ? s.segOn : ''}`} onClick={() => setModel(m.id)}>{m.label}</button>
						))}
					</div>
					{!!models.find((m) => m.id === model)?.hint && <span className={s.why}>{models.find((m) => m.id === model).hint}</span>}
			</div>
			<div className={pageStyles.block}>
				<span>Thoroughness</span>
				<div className={pageStyles.pickerSeg} role='group' aria-label='Thoroughness'>
					{SCHED_EFFORTS.map((e) => (
						<button key={e} type='button' aria-pressed={effort === e}
							className={effort === e ? pageStyles.pickerSegOn : ''} onClick={() => setEffort(e)}>{effortLabel(e)}</button>
					))}
				</div>
			</div>
			<CustomerFacingSwitch value={customerFacing} onChange={setCustomerFacing} />
			<div className={pageStyles.block}>
				<span>Repeat</span>
				<RepeatPicker f={rf} set={(p) => setRf((o) => ({ ...o, ...p }))} />
				<div className={s.sentence} aria-live='polite'><ScheduleRoundedIcon fontSize='inherit' aria-hidden /> <RepeatSentence preview={preview} /></div>
			</div>
			<label className={pageStyles.check}>
				<input type='checkbox' checked={emailOn} onChange={(e) => setEmailOn(e.target.checked)} /> Email the answer
			</label>
			{emailOn && (
				<div className={s.emailBox}>
					<label className={pageStyles.block}>Send to (comma-separated, up to 20)
						<textarea rows={2} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder='ops@company.com, asha@company.com'
							aria-invalid={!!badEmail} aria-describedby={badEmail ? 'sched-email-err' : undefined} />
					</label>
					{!!badEmail && <div id='sched-email-err' className={s.repeatBad}>"{badEmail}" doesn't look like an email address.</div>}
					<label className={pageStyles.block}>Subject
						<span className={s.inline}>
							<input className={s.grow} value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} placeholder='e.g. Pending indents {date}' />
							<span title={!draft.title && !draft.prompt ? 'Add a title or a question first.' : 'Suggests a subject. Counts toward today’s runs.'}>
								<button type='button' className={pageStyles.btnGhost} onClick={suggest} disabled={suggesting || (!draft.title && !draft.prompt)}>
									<AutoAwesomeRoundedIcon fontSize='inherit' aria-hidden /> {suggesting ? 'Thinking…' : 'Suggest'}
								</button>
							</span>
						</span>
						<span className={pageStyles.muted}>{'{date}'} becomes the run date.</span>
					</label>
					<label className={pageStyles.check}>
						<input type='checkbox' checked={onlyIfChanged} onChange={(e) => setOnlyIfChanged(e.target.checked)} /> Only email when something changed
					</label>
					<label className={pageStyles.check}>
						<input type='checkbox' checked={attachFiles} onChange={(e) => setAttachFiles(e.target.checked)} /> Attach files Claude makes
					</label>
					{(!existing || !existing.approvedFirstRun) && (
						<div className={pageStyles.muted}>The first real run emails only you. Once you say it looks right, everyone on the list gets it.</div>
					)}
				</div>
			)}
			<TestRunResult test={t.test} />
			<div className={s.actions}>
				<span title={problem || 'Runs it once now so you can check the answer. Counts toward today’s runs.'}>
					<button type='button' className={pageStyles.btnGhost} onClick={() => t.start(draft)} disabled={!!problem || t.starting || t.going}>
						<ScienceRoundedIcon fontSize='inherit' aria-hidden /> {t.test ? 'Test again' : 'Test run'}
					</button>
				</span>
				<span title={saveWhy}>
					<button type='button' className={pageStyles.btnPrimary} onClick={save} disabled={!!saveWhy || saving}>
						{saving ? 'Saving…' : existing ? 'Save changes' : 'Save and start'}
					</button>
				</span>
				<button type='button' className={pageStyles.btnGhost} onClick={onClose}>Cancel</button>
			</div>
			{!!saveWhy && <div className={s.why}>{saveWhy}</div>}
		</section>
	);
}

// ---- run history of one task ----
function History({ run, sched, onOpenChat }) {
	const [runs, setRuns] = useState(null);
	const [err, setErr] = useState('');
	const [sending, setSending] = useState('');
	const load = useCallback(() => {
		setErr('');
		return run('schedHistory', { scheduleId: sched.id }, { quiet: true })
			.then((d) => setRuns(d?.runs || [])).catch((e) => setErr(String(e || 'Could not load the runs.')));
	}, [run, sched.id]);
	// Reload when the task's last run changes or a run starts/stops, and keep checking while a row is still going.
	useEffect(() => { load(); }, [load, sched.lastRun?.runId, sched.lastRun?.status, sched.busy]);
	const going = !!runs?.some((r) => ['queued', 'running'].includes(r.status));
	useEffect(() => {
		if (!going) return undefined;
		const t = setInterval(load, HIST_POLL_MS);
		return () => clearInterval(t);
	}, [going, load]);
	const sendAnyway = (r) => {
		if (sending) return;
		if (!window.confirm("Send this email anyway? Only do this if you checked it holds no other customer's data.")) return;
		setSending(r.runId);
		run('schedSendAnyway', { scheduleId: sched.id, runId: r.runId }).then(load).catch(() => {}).finally(() => setSending(''));
	};
	if (err && !runs) {
		return (
			<div className={s.repeatBad}>{err} <button type='button' className={s.linkBtn} onClick={load}>Try again</button></div>
		);
	}
	if (!runs) return <div className={pageStyles.muted}>Loading runs…</div>;
	if (!runs.length) return <div className={pageStyles.muted}>No runs yet. The first one is {sched.nextRunText ? `on ${sched.nextRunText}` : 'coming up'}.</div>;
	return (
		<ol className={s.history}>
			{!!err && <li className={s.repeatBad}>{err} <button type='button' className={s.linkBtn} onClick={load}>Try again</button></li>}
			{runs.map((r) => (
				<li key={r.runId} className={s.histItem}>
					<span className={`${s.badge} ${runTone(r.status)}`}>{runStatusText(r)}</span>
					<span className={s.histMain}>
						<span>{fullTime(r.startedAt || r.queuedAt, sched.timeZone)}{r.trigger === 'runNow' ? ' · Run now' : ''}</span>
						{!!r.emailNote && <span className={s.why}>Email: {r.emailNote}</span>}
					</span>
					{r.status === 'held' && <button type='button' className={pageStyles.btnGhost} disabled={!!sending} onClick={() => sendAnyway(r)}>{sending === r.runId ? 'Sending…' : 'Send anyway'}</button>}
					{!!r.chatId && <button type='button' className={pageStyles.btnGhost} onClick={() => onOpenChat(r.chatId)}>Open chat</button>}
				</li>
			))}
			<li className={pageStyles.muted}>Shows the last 30 runs.</li>
		</ol>
	);
}

// ---- one task in the list ----
function ScheduleItem({ sched, run, limits, highlight, onEdit, onChanged, onRemoved, onOpenChat, itemRef }) {
	const [busy, setBusy] = useState('');
	const [showHist, setShowHist] = useState(false);
	const act = (action, label) => {
		setBusy(label);
		return run(action, { scheduleId: sched.id }).then((d) => d?.schedule && onChanged(d.schedule)).catch(() => {}).finally(() => setBusy(''));
	};
	const del = () => {
		if (!window.confirm(`Delete "${sched.title}"? It stops repeating. Past run chats stay.`)) return;
		setBusy('delete');
		run('schedDelete', { scheduleId: sched.id }).then(() => onRemoved(sched.id)).catch(() => {}).finally(() => setBusy(''));
	};
	const paused = sched.state !== 'active';
	const lr = sched.lastRun;
		const dayLimit = limits?.runsPerOrgPerDay ? ` (${limits.runsPerOrgPerDay} a day per customer)` : '';
	const runWhy = sched.busy ? 'A run is already waiting or running.' : paused ? 'Resume it first.' : `Runs it once now. Waits its turn and counts toward the daily run limit${dayLimit}.`;
	const lookWhy = !lr || lr.status !== 'ok' ? 'Wait for one good run first.' : '';
	return (
		<article ref={itemRef} className={`${s.item} ${highlight ? s.itemHi : ''} ${paused ? s.itemPaused : ''}`} tabIndex={-1}>
			<div className={s.itemTop}>
				<div className={s.itemTitleWrap}>
					<div className={s.itemTitle}>{sched.title}</div>
					<div className={s.itemRepeat}><ScheduleRoundedIcon fontSize='inherit' aria-hidden /> {sched.repeatText}</div>
				</div>
				{sched.state === 'paused' && <span className={`${s.badge} ${s.toneMuted}`}>Paused</span>}
				{sched.state === 'auto-paused' && <span className={`${s.badge} ${s.toneBad}`}>Paused automatically</span>}
				{sched.busy && <span className={`${s.badge} ${s.toneInfo}`}>Running</span>}
			</div>
			{!!sched.prompt && <div className={`${s.itemPrompt} ${s.clamp}`}>{sched.prompt}</div>}
			{sched.state === 'auto-paused' && !!sched.pausedReason && <div className={s.repeatBad}>{sched.pausedReason}</div>}
			<div className={s.receiptRows}>
				<Row k='Next run'>{paused ? 'Paused' : sched.nextRunText || '—'}</Row>
				<Row k='Last run'>
					{lr ? <><span className={`${s.badge} ${runTone(lr.status)}`}>{runStatusText(lr)}</span> <span className={s.why}>{fullTime(lr.at, sched.timeZone)}</span>
						{!!lr.chatId && <button type='button' className={s.linkBtn} onClick={() => onOpenChat(lr.chatId)}>Open</button>}</>
						: <span className={pageStyles.muted}>Not run yet</span>}
				</Row>
				<Row k='Answer style'>{sched.customerFacing ? 'Customer-facing (plain business words)' : 'Technical (queries and proof)'}</Row>
				<Row k='Email'>
					{sched.emailTo?.length ? `${sched.emailTo.length === 1 ? sched.emailTo[0] : `${sched.emailTo.length} people`}${sched.emailOnlyIfChanged ? ' · only when changed' : ''}` : 'None'}
				</Row>
				<Row k='Customer'>{sched.customerIds?.length > 1 ? `Customers ${sched.customerIds.join(', ')}` : (sched.customerName || sched.customer?.name || `Customer ${sched.customerId}`)}{sched.createdByName ? ` · made by ${sched.createdByName}` : ''}</Row>
			</div>
			{!sched.approvedFirstRun && !!sched.emailTo?.length && (
				<div className={`${pageStyles.banner} ${pageStyles.bannerInfo}`}>
					<span>Emails go only to the creator until you confirm the report looks right.</span>
					<button type='button' className={pageStyles.btnPrimary} disabled={!!busy || !lr || lr.status !== 'ok'}
												title={lookWhy} onClick={() => act('schedLooksRight', 'ok')}>Looks right, send to everyone</button>
					{!!lookWhy && <span className={s.why}>{lookWhy}</span>}
				</div>
			)}
			<div className={s.actions}>
				<span title={runWhy}>
					<button type='button' className={pageStyles.btnGhost} onClick={() => act('schedRunNow', 'run')} disabled={!!busy || sched.busy || paused} aria-describedby={`run-why-${sched.id}`}>
						<PlayArrowRoundedIcon fontSize='inherit' aria-hidden /> {busy === 'run' ? 'Starting…' : 'Run now'}
					</button>
				</span>
				{paused ? (
					<button type='button' className={pageStyles.btnGhost} onClick={() => act('schedResume', 'resume')} disabled={!!busy}>
						<PlayArrowRoundedIcon fontSize='inherit' aria-hidden /> Resume
					</button>
				) : (
					<button type='button' className={pageStyles.btnGhost} onClick={() => act('schedPause', 'pause')} disabled={!!busy}>
						<PauseRoundedIcon fontSize='inherit' aria-hidden /> Pause
					</button>
				)}
				<button type='button' className={pageStyles.btnGhost} onClick={() => onEdit(sched)} disabled={!!busy}>
					<EditRoundedIcon fontSize='inherit' aria-hidden /> Edit
				</button>
				<button type='button' className={pageStyles.btnGhost} onClick={() => setShowHist((v) => !v)} aria-expanded={showHist}>
					<HistoryRoundedIcon fontSize='inherit' aria-hidden /> History
				</button>
				{sched.hasMemory && (
					<button type='button' className={pageStyles.btnGhost} disabled={!!busy}
						title='The next run starts fresh, without comparing to the last one.' onClick={() => act('schedForgetMemory', 'forget')}>
						Forget last run
					</button>
				)}
				<button type='button' className={`${pageStyles.btnGhost} ${s.dangerGhost}`} onClick={del} disabled={!!busy} aria-label={`Delete ${sched.title}`}>
					<DeleteOutlineIcon fontSize='inherit' aria-hidden /> Delete
				</button>
			</div>
						<div id={`run-why-${sched.id}`} className={s.why}>{runWhy}</div>
			{showHist && <History run={run} sched={sched} onOpenChat={onOpenChat} />}
		</article>
	);
}

// ---- the tab ----
// formSeed: a draft to open in the form (from a chat card's Edit); focusSched: a just-approved task to show and highlight.
// The page keeps this tab mounted (hidden) while you chat, so open forms and test runs survive.
export function SchedulesTab({ run, models, focusSched, onFocusUsed, formSeed, onFormSeedUsed, onOpenChat, onSchedules }) {
	const [data, setData] = useState(null);   // {schedules, banner, limits}
	const [loadErr, setLoadErr] = useState('');
	const [form, setForm] = useState(null);   // {initial, existing}
	const [hi, setHi] = useState(null);
	const refs = useRef({});
	const newBtn = useRef(null);
	const dirtyRef = useRef(false);
	const seq = useRef(0);        // numbers each list request
	const localRev = useRef(0);   // request number at our last local change: older answers are stale
	const applied = useRef(0);

	const load = useCallback(() => {
		const id = ++seq.current;
		return run('schedList', {}, { quiet: true })
			.then((d) => {
				if (id < localRev.current || id < applied.current) return;
				applied.current = id;
				setData(d || { schedules: [] }); setLoadErr('');
			})
			.catch((e) => setLoadErr(String(e || 'Could not load scheduled tasks.')));
	}, [run]);
	useEffect(() => { load(); const t = setInterval(load, LIST_POLL_MS); return () => clearInterval(t); }, [load]);

	const upsert = (sc) => {
		localRev.current = ++seq.current;
		setData((d) => {
			const list = d?.schedules || [];
			const has = list.some((x) => x.id === sc.id);
			return { ...(d || {}), schedules: has ? list.map((x) => (x.id === sc.id ? sc : x)) : [sc, ...list] };
		});
	};
	const remove = (id) => {
		localRev.current = ++seq.current;
		setData((d) => ({ ...d, schedules: (d?.schedules || []).filter((x) => x.id !== id) }));
	};

	// Opening a form over an edited one asks first.
	const openForm = (next) => {
		if (form && dirtyRef.current && !window.confirm('You have unsaved changes in the open form. Discard them?')) return false;
		setForm(next);
		return true;
	};
	const closeForm = () => {
		const id = form?.existing?.id;
		setForm(null);
		setTimeout(() => (id && refs.current[id] ? refs.current[id] : newBtn.current)?.focus(), 0);
	};

	useEffect(() => {
		if (!formSeed) return;
		openForm({ initial: formSeed });
		onFormSeedUsed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formSeed]);
	useEffect(() => {
		if (!focusSched) return;
		upsert(focusSched); setHi(focusSched.id); onFocusUsed();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusSched]);
	// Scroll the highlighted task into view, then fade the highlight.
	useEffect(() => {
		const el = hi && refs.current[hi];
		if (!el) return undefined;
		el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		el.focus({ preventScroll: true });
		const t = setTimeout(() => setHi(null), 4000);
		return () => clearTimeout(t);
	}, [hi]);
	useEffect(() => { if (onSchedules && data) onSchedules(data.schedules || []); }, [data, onSchedules]);

	const list = data?.schedules || [];
	const banner = data?.banner;
	const limits = data?.limits;
	return (
		<div className={s.tab}>
			<div className={s.tabInner}>
				<div className={s.tabHead}>
					<div>
						<h2 className={s.h2}>Scheduled tasks</h2>
						<div className={pageStyles.muted}>Questions Server Chat asks by itself on a repeat. Each run is saved as a chat{limits ? ` · up to ${limits.maxActivePerOrg} active per customer` : ''}.</div>
					</div>
					{!form && (
						<button type='button' ref={newBtn} className={pageStyles.btnPrimary} onClick={() => openForm({ initial: null })}>
							<AddRoundedIcon fontSize='inherit' aria-hidden /> New task
						</button>
					)}
				</div>

				{!!banner?.message && (
					<div className={`${pageStyles.banner} ${banner.timerStopped || banner.pausedAll ? pageStyles.bannerErr : pageStyles.bannerWarn}`} role='status'>
						<span>{banner.message}</span>
					</div>
				)}
				{!!banner?.timerSetup && (
					<div className={`${pageStyles.banner} ${pageStyles.bannerInfo}`} style={{ display: 'block' }}>
						<div>{banner.timerSetup.howTo}</div>
						<CopyLine text={banner.timerSetup.chmod} label='Copy the first command' />
						<CopyLine text={banner.timerSetup.cronLine} label='Copy the line to paste' />
					</div>
				)}
				{!!loadErr && (
					<div className={`${pageStyles.banner} ${pageStyles.bannerErr}`} role='alert'>
						<span>{loadErr}</span>
						<button type='button' className={pageStyles.btnGhost} onClick={load}>Try again</button>
					</div>
				)}

				{form && (
					<ScheduleForm key={form.existing?.id || 'new'} run={run} models={models} initial={form.existing || form.initial} existing={form.existing}
						dirtyRef={dirtyRef} onClose={closeForm}
						onSaved={(sc) => { upsert(sc); setForm(null); setHi(sc.id); }} />
				)}

				{!data && !loadErr && <div className={pageStyles.muted}>Loading scheduled tasks…</div>}
				{data && !list.length && !form && (
					<div className={s.empty}>
						<ScheduleRoundedIcon className={s.emptyIcon} aria-hidden />
						<strong>No scheduled tasks yet</strong>
						<span className={pageStyles.muted}>Create one here, or press “Repeat this” under any answer in a chat.</span>
						<button type='button' className={pageStyles.btnPrimary} onClick={() => openForm({ initial: null })}>New task</button>
					</div>
				)}
				{list.map((sc) => (
					<ScheduleItem key={sc.id} sched={sc} run={run} limits={limits} highlight={hi === sc.id}
						itemRef={(el) => { if (el) refs.current[sc.id] = el; else delete refs.current[sc.id]; }}
						onEdit={(x) => openForm({ existing: x })} onChanged={upsert} onRemoved={remove} onOpenChat={onOpenChat} />
				))}
			</div>
		</div>
	);
}
